// Google Tag Manager at parity with the Help before 2.0: the same container on the same
// public pages. The container, not this code, decides which tags run (GA4 among them).
//
// What reaches Google is bounded here. GTM only ever sees the page address and the events
// pushed below; chat questions, answers, share links and chat API calls are never pushed.
// The one way a chat question could still leak is the address itself, because GA4 records
// the full page URL — hence mayLoadTagManager.

import type { ChatHomeTile } from "./home-tiles.js";

export const TAG_MANAGER_ORIGIN = "https://www.googletagmanager.com";

export type DataLayerEntry = Record<string, unknown>;

export interface DataLayerHost {
  dataLayer?: DataLayerEntry[];
}

declare global {
  interface Window extends DataLayerHost {}
}

/**
 * Whether GTM may load on a page opened at this address. `?ask=` carries a chat question
 * typed elsewhere (Profit, InSite) and `#share=` carries a share token; neither may end up
 * in a recorded page URL, so such a visit is left unmeasured. `?q=` stays measured: it is the
 * keyword entry the old Help already sent along.
 */
export function mayLoadTagManager(url: URL): boolean {
  if (url.searchParams.has("ask")) return false;
  const fragment = new URLSearchParams(url.hash.replace(/^#/, ""));
  return !fragment.has("share");
}

export function tagManagerScriptUrl(containerId: string): string {
  return `${TAG_MANAGER_ORIGIN}/gtm.js?id=${encodeURIComponent(containerId)}`;
}

/**
 * Hands an event to GTM. The data layer only exists when the bootstrap loaded the container,
 * so on pages without GTM (embed, CMS, local development, an unmeasured visit) this does
 * nothing instead of queueing events for a container that never comes.
 */
export function pushTagManagerEvent(
  host: DataLayerHost,
  entry: DataLayerEntry,
): void {
  if (!Array.isArray(host.dataLayer)) return;
  host.dataLayer.push(entry);
}

// Title and id come from the CMS, not from the visitor, so they are safe to send. The
// question behind the tile is left out: it is what gets sent to the chat.
export function homeTileClickEvent(
  tile: Pick<ChatHomeTile, "id" | "title">,
): DataLayerEntry {
  return { event: "home_tile_click", tile_id: tile.id, tile_title: tile.title };
}

export interface HomeTileClickReporter {
  /** Reports the click unless this home screen already reported one. */
  report(tile: Pick<ChatHomeTile, "id" | "title">): void;
  /** Call when the home screen shows again, so its next tile click counts. */
  reset(): void;
}

/**
 * A tile click sends its question and leaves the home screen, so one home screen yields at
 * most one tile click. A double click can land before the list re-renders as disabled; the
 * reporter makes sure that still counts once.
 */
export function createHomeTileClickReporter(
  host: DataLayerHost,
): HomeTileClickReporter {
  let reported = false;
  return {
    report(tile) {
      if (reported) return;
      reported = true;
      pushTagManagerEvent(host, homeTileClickEvent(tile));
    },
    reset() {
      reported = false;
    },
  };
}
